import {Trans} from '@lingui/macro';
import {isEqual} from 'lodash';
import {DateTime} from 'luxon';
import React, {ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {collectEnumValues} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdownWithHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownWithHeader';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {SmartDateTimeFormatterContainer} from '../../../../../srcCommon/formatters/dateTime/smartDateTimeFormatterContainer';
import {DropdownItemSecondaryContent} from '../../../../components/common/dropdowns/dropdownItemSecondaryContent';
import {SnoozePresetParam} from '../../../editors/commons/paramModel';
import {GenericSingleParameterProps} from './genericParameter';

/*
 * Types.
 */

interface PresetItem {
  id: string;
  name: ReactNode;
  preset: SnoozePreset;
}

interface SnoozePresetParameterProps
  extends Omit<GenericSingleParameterProps<PresetItem>, 'selectedItem' | 'onChange'> {
  value?: SnoozePresetParam;
  onSnoozePresetChange: HandlerOf<SnoozePresetParam>;
}

enum RuleSnoozePresetsEnum {
  THREE_HOURS_LATER = 'three_hours_later',
  SIX_HOURS_LATER = 'six_hours_later',
  ONE_DAY_LATER = 'one_day_later',
  THREE_DAYS_LATER = 'three_days_later',
  ONE_WEEK_LATER = 'one_week_later',
}

enum RelativeTimeUnitsEnum {
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
}

interface SnoozeOffset {
  week: number;
  day: number;
  hour: number;
  minute: number;
}

interface SnoozePreset {
  relativeTo: RelativeTimeUnitsEnum;
  offset: SnoozeOffset;
}

/*
 * Constants.
 */

const SnoozePresets: Record<RuleSnoozePresetsEnum, SnoozePreset> = {
  [RuleSnoozePresetsEnum.THREE_HOURS_LATER]: {
    relativeTo: RelativeTimeUnitsEnum.MINUTE,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 3,
      [RelativeTimeUnitsEnum.DAY]: 0,
      [RelativeTimeUnitsEnum.WEEK]: 0,
    },
  },
  [RuleSnoozePresetsEnum.SIX_HOURS_LATER]: {
    relativeTo: RelativeTimeUnitsEnum.MINUTE,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 6,
      [RelativeTimeUnitsEnum.DAY]: 0,
      [RelativeTimeUnitsEnum.WEEK]: 0,
    },
  },
  [RuleSnoozePresetsEnum.ONE_DAY_LATER]: {
    relativeTo: RelativeTimeUnitsEnum.DAY,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 9,
      [RelativeTimeUnitsEnum.DAY]: 1,
      [RelativeTimeUnitsEnum.WEEK]: 0,
    },
  },
  [RuleSnoozePresetsEnum.THREE_DAYS_LATER]: {
    relativeTo: RelativeTimeUnitsEnum.DAY,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 9,
      [RelativeTimeUnitsEnum.DAY]: 3,
      [RelativeTimeUnitsEnum.WEEK]: 0,
    },
  },
  [RuleSnoozePresetsEnum.ONE_WEEK_LATER]: {
    relativeTo: RelativeTimeUnitsEnum.WEEK,
    offset: {
      [RelativeTimeUnitsEnum.MINUTE]: 0,
      [RelativeTimeUnitsEnum.HOUR]: 9,
      [RelativeTimeUnitsEnum.DAY]: 0,
      [RelativeTimeUnitsEnum.WEEK]: 1,
    },
  },
};

const PresetLabels: Record<RuleSnoozePresetsEnum, ReactNode> = {
  [RuleSnoozePresetsEnum.THREE_HOURS_LATER]: <Trans id="rule-snooze-preset-3-hours">3 hours later</Trans>,
  [RuleSnoozePresetsEnum.SIX_HOURS_LATER]: <Trans id="rule-snooze-preset-6-hours">6 hours later</Trans>,
  [RuleSnoozePresetsEnum.ONE_DAY_LATER]: <Trans id="rule-snooze-preset-1-day-later">1 day later</Trans>,
  [RuleSnoozePresetsEnum.THREE_DAYS_LATER]: <Trans id="rule-snooze-preset-3-days-later">3 days later</Trans>,
  [RuleSnoozePresetsEnum.ONE_WEEK_LATER]: <Trans id="rule-snooze-preset-1-week-later">1 week later</Trans>,
};

const items: PresetItem[] = collectEnumValues(RuleSnoozePresetsEnum).map((item) => {
  const name = PresetLabels[item];
  const preset = SnoozePresets[item];
  return {id: item, name, preset};
});

/*
 * Style.
 */

const DropdownBoxCoordinatorWrapper = styled.div`
  font-weight: ${fontWeights.normal};
`;

const StyledDropdownItem = styled(DropdownItem)`
  font-weight: ${(p) => (p.isChecked ? fontWeights.semibold : fontWeights.normal)};
`;

interface ContentStyleProps {
  $color: string;
}
const StyledContentDiv = styled.div<ContentStyleProps>`
  color: ${(p) => p.$color};
`;

const StyledSecondaryContentDiv = styled(StyledContentDiv)`
  text-align: right;
`;

/*
 * Component.
 */

export const SnoozePresetParameter = (props: SnoozePresetParameterProps) => {
  const {value, onSnoozePresetChange, isDisabled} = props;

  const selectedItem = useMemo(() => items.find((item) => isEqual(item.preset, value)), [value]);

  return (
    <DropdownBoxCoordinatorWrapper>
      <DropdownBoxCoordinator
        shouldDisablePadding
        value={renderValue(selectedItem)}
        renderDropdown={(transferProps) => renderDropdown(transferProps, onSnoozePresetChange, selectedItem)}
        isDisabled={isDisabled}
      />
    </DropdownBoxCoordinatorWrapper>
  );
};

function renderValue(selectedItem?: PresetItem) {
  if (!selectedItem) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledContentDiv $color={theme.greys.shade60}>
            <Trans id="rule-snooze-preset-placeholder">Select time</Trans>
          </StyledContentDiv>
        )}
      </UserThemeConsumer>
    );
  }
  return selectedItem.name;
}

function renderDropdown(
  transferProps: PopoverTransferProps,
  onChange: HandlerOf<SnoozePresetParam>,
  selectedItem?: PresetItem,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <SimpleDropdownWithHeader
          {...transferProps}
          title={<Trans id="snooze-presets-title">Set a time</Trans>}
          width={300}
        >
          {items.map((item) => {
            const isChecked = selectedItem?.id === item.id;
            return (
              <StyledDropdownItem key={item.id} onSelect={() => onChange(item.preset)} isChecked={isChecked}>
                <DropdownItemContent isChecked={isChecked}>
                  <StyledContentDiv $color={theme.greys.shade80}>{item.name}</StyledContentDiv>
                </DropdownItemContent>
                {maybeRenderTime(item.preset)}
              </StyledDropdownItem>
            );
          })}
        </SimpleDropdownWithHeader>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderTime(preset: SnoozePreset) {
  if (preset.relativeTo === RelativeTimeUnitsEnum.MINUTE) {
    return null;
  }

  const time = DateTime.local().set({
    hour: preset.offset.hour,
    minute: preset.offset.minute,
  });

  return (
    <UserThemeConsumer>
      {(theme) => (
        <DropdownItemSecondaryContent>
          <StyledSecondaryContentDiv $color={theme.greys.shade60}>
            <SmartDateTimeFormatterContainer value={time.toMillis()} />
          </StyledSecondaryContentDiv>
        </DropdownItemSecondaryContent>
      )}
    </UserThemeConsumer>
  );
}
